import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Filter, Users, TrendingUp, CheckCircle2, XCircle } from 'lucide-react';
import { getLeads, createLead, updateLead, deleteLead, getLeadStats, getUsers, getSectors } from '../services/api';

const statusLabels = {
  new: { label: 'Novo', color: 'bg-blue-100 text-blue-700' },
  contacted: { label: 'Contatado', color: 'bg-yellow-100 text-yellow-700' },
  qualified: { label: 'Qualificado', color: 'bg-purple-100 text-purple-700' },
  converted: { label: 'Convertido', color: 'bg-green-100 text-green-700' },
  lost: { label: 'Perdido', color: 'bg-red-100 text-red-700' }
};

const sourceLabels = {
  manual: 'Manual',
  website: 'Website',
  whatsapp: 'WhatsApp',
  campaign: 'Campanha'
};

function Leads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', source: 'manual', status: 'new',
    assigned_to: '', sector_id: '', lost_reason: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [leadsRes, statsRes, usersRes, sectorsRes] = await Promise.all([
        getLeads({ search, status: filterStatus }),
        getLeadStats(),
        getUsers(),
        getSectors()
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setSectors(sectorsRes.data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateLead(editing.id, form);
      } else {
        await createLead(form);
      }
      setShowModal(false);
      setEditing(null);
      setForm({ name: '', phone: '', email: '', source: 'manual', status: 'new', assigned_to: '', sector_id: '', lost_reason: '' });
      loadData();
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
  };

  const handleEdit = (lead) => {
    setEditing(lead);
    setForm({
      name: lead.name, phone: lead.phone, email: lead.email || '',
      source: lead.source, status: lead.status,
      assigned_to: lead.assigned_to || '', sector_id: lead.sector_id || '',
      lost_reason: lead.lost_reason || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este lead?')) {
      try { await deleteLead(id); loadData(); } catch (err) { alert('Erro ao excluir'); }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CRM de Leads</h1>
          <p className="text-gray-500">Gerencie seus leads e acompanhe o funil de vendas</p>
        </div>
        <button onClick={() => { setEditing(null); setForm({ name: '', phone: '', email: '', source: 'manual', status: 'new', assigned_to: '', sector_id: '' }); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 shadow-lg shadow-green-100">
          <Plus size={18} /> Novo Lead
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
          <p className="text-xs text-gray-500 mb-1">Total</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
          <p className="text-xs text-blue-600 mb-1">Novos</p>
          <p className="text-2xl font-bold text-blue-900">{stats.new || 0}</p>
        </div>
        <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-100">
          <p className="text-xs text-yellow-600 mb-1">Contatados</p>
          <p className="text-2xl font-bold text-yellow-900">{stats.contacted || 0}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
          <p className="text-xs text-purple-600 mb-1">Qualificados</p>
          <p className="text-2xl font-bold text-purple-900">{stats.qualified || 0}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-xl border border-green-100">
          <p className="text-xs text-green-600 mb-1">Convertidos</p>
          <p className="text-2xl font-bold text-green-900">{stats.converted || 0}</p>
        </div>
        <div className="bg-red-50 p-4 rounded-xl border border-red-100">
          <p className="text-xs text-red-600 mb-1">Perdidos</p>
          <p className="text-2xl font-bold text-red-900">{stats.lost || 0}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="text" placeholder="Buscar por nome, telefone ou email..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none">
            <option value="">Todos os status</option>
            {Object.entries(statusLabels).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>
          <button onClick={loadData} className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700">
            Filtrar
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-6 py-4 font-semibold">Nome</th>
                <th className="px-6 py-4 font-semibold">Contato</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold">Origem</th>
                <th className="px-6 py-4 font-semibold">Responsável</th>
                <th className="px-6 py-4 font-semibold">Setor</th>
                <th className="px-6 py-4 font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {leads.map(lead => (
                <tr key={lead.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold text-sm">
                        {lead.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-gray-900">{lead.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-900">{lead.phone}</p>
                    {lead.email && <p className="text-xs text-gray-500">{lead.email}</p>}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${statusLabels[lead.status]?.color}`}>
                      {statusLabels[lead.status]?.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{sourceLabels[lead.source]}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{lead.assigned_to_name || '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{lead.sector_name || '—'}</td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(lead)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 size={16} /></button>
                      <button onClick={() => handleDelete(lead.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {leads.length === 0 && <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-400">Nenhum lead encontrado.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold mb-4">{editing ? 'Editar Lead' : 'Novo Lead'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input type="text" placeholder="Nome completo *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required
                  className="p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" />
                <input type="tel" placeholder="Telefone *" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required
                  className="p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" />
                <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                  className="p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none" />
                <select value={form.source} onChange={e => setForm({...form, source: e.target.value})}
                  className="p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none">
                  <option value="manual">Manual</option>
                  <option value="website">Website</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="campaign">Campanha</option>
                </select>
                <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                  className="p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none">
                  {Object.entries(statusLabels).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
                <select value={form.assigned_to} onChange={e => setForm({...form, assigned_to: e.target.value})}
                  className="p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none">
                  <option value="">Sem responsável</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
                <select value={form.sector_id} onChange={e => setForm({...form, sector_id: e.target.value})}
                  className="p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none">
                  <option value="">Sem setor</option>
                  {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              {form.status === 'lost' && (
                <textarea placeholder="Motivo da perda" value={form.lost_reason} onChange={e => setForm({...form, lost_reason: e.target.value})} rows={2}
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none resize-none" />
              )}
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
                <button type="submit" className="px-5 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Leads;